import React, { useState, useRef, useEffect } from "react";

export default function VoiceInput({ onTranscript, className = "", size = "md" }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onresult = (event) => {
        let finalTranscript = "";
        let interimTranscript = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        if (finalTranscript) {
          onTranscript(finalTranscript);
        } else if (interimTranscript) {
          onTranscript(interimTranscript, true);
        }
      };

      recognition.onerror = (event) => {
        if (event.error !== "no-speech") {
          console.warn("Speech recognition error:", event.error);
        }
        setListening(false);
      };

      recognition.onend = () => {
        setListening(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
    };
  }, [onTranscript]);

  const toggleListening = () => {
    if (!recognitionRef.current) return;

    if (listening) {
      recognitionRef.current.stop();
      setListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setListening(true);
      } catch (e) {
        // Already started
        recognitionRef.current.stop();
        setTimeout(() => {
          recognitionRef.current.start();
          setListening(true);
        }, 100);
      }
    }
  };

  if (!supported) return null;

  const sizes = {
    sm: "w-7 h-7",
    md: "w-9 h-9",
    lg: "w-11 h-11",
  };

  return (
    <button
      type="button"
      onClick={toggleListening}
      title={listening ? "Stop listening" : "Voice input"}
      className={`relative flex items-center justify-center rounded-full transition-all duration-300 ${sizes[size]} ${
        listening
          ? "bg-red-500/20 border border-red-500/50 text-red-400 shadow-[0_0_12px_rgba(239,68,68,0.3)]"
          : "bg-white/5 border border-white/10 text-gray-400 hover:text-white hover:border-white/25 hover:bg-white/10"
      } ${className}`}
    >
      {/* Pulsing ring when listening */}
      {listening && (
        <>
          <span className="absolute inset-0 rounded-full border border-red-500/40 animate-ping" />
          <span className="absolute inset-[-4px] rounded-full border border-red-500/20 animate-pulse" />
        </>
      )}

      {/* Mic icon */}
      <svg
        className={`relative z-10 ${size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"}`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </svg>
    </button>
  );
}
